export class Browser {
  static ToFormData(data) {
    const formData = new FormData();

    for (const key in data) {
      if (data[key] != null) {
        if (Array.isArray(data[key])) {
          if (data[key].length) {
            for (const value of data[key]) {
              formData.append(key, value);
            }
          }
        } else {
          formData.append(key, data[key]);
        }
      }
    }

    return formData;
  }

  static #AddMessage(type, title, message, options) {
    localStorage.setItem("persistent-message", JSON.stringify({
      type,
      title,
      message,
      options
    }));
  }

  static AddSuccessMessage(title, message, options = {})
  {
    this.#AddMessage("success", title, message, options);
  }

  static AddInfoMessage(title, message, options = {})
  {
    this.#AddMessage("info", title, message, options);
  }

  static AddWarningMessage(title, message, options = {})
  {
    this.#AddMessage("warning", title, message, options);
  }

  static AddErrorMessage(title, message, options = {})
  {
    this.#AddMessage("error", title, message, options);
  }

  static Refresh() {
    location.href = location.href;
  }

  static AddCssRule(element, rule) {
    element.id = element.id || ("di" + crypto.randomUUID().slice(0, 8));

    let sheet = (document.getElementById("stAuxilium") ?? document.head.appendChild(document.createElementWithContents("style", null, { id: "stAuxilium" }))).sheet;

    sheet.insertRule(`#${element.id} { ${rule} }`, sheet.cssRules.length);
  }

  static ResolveRelativeURL(url, includeCulture) {
    if (!url) { return "" };
    url = url.replace("~//", "~/");

    let tildaReplacement = document.documentElement.dataset.serverPath || location.origin;

    if (tildaReplacement.endsWith("/")) { tildaReplacement = tildaReplacement.substring(0, tildaReplacement.length - 1); }
    if (includeCulture && document.documentElement.dataset.culture) { tildaReplacement += `/${document.documentElement.dataset.culture}`; }

    if (url.startsWith("~")) {
      return url.replace("~", tildaReplacement);
    } else if (url.startsWith(".")) {
      return new URL(url, location).toString();
    } else {
      return url;
    }
  }

  static GetRouteID = (url) => parseInt(this.GetPaths(url).find(x => !isNaN(parseInt(x))));

  static SetTestFunction(fn) {
    const existing = document.querySelector(".btn--test");

    if (!existing) {
      document.body.appendChild(document.createElementWithContents("chip-button", "Test Button", {
        onclick: fn,
        variation: "danger",
        className: "btn--test"
      }));
    } else {
      existing.onclick = fn;
    }
  }

  static SubmitSSO(clientID, email) {
    const form = document.createElementWithContents("form",
      `<input type="hidden"  name="client_id" value="${clientID}"/><input type="hidden" name="scope" value="openid User.Read profile email"/><input type="hidden" name="response_type" value="code"/><input type="hidden" name="redirect_uri" value="${this.ResolveRelativeURL("~/oauth")}"/><input type="hidden" name="login_hint" value="${email ?? ""}"/><input type="hidden" name="state" value="${this.GetURLParameter("ReturnUrl") ?? "/"}"/>`, {
      id: "frmSSOAgent",
      name: "frmSSOAgent",
      method: "post",
      target: "_self",
      action: "https://login.microsoftonline.com/chipside.onmicrosoft.com/oauth2/v2.0/authorize"
    });

    document.body.appendChild(form);
    form.submit();
  }

  static SetFlavour(flavour, local) {
    if (!local) { localStorage.setItem("flavour", flavour?.toLowerCase()); }

    const target = document.querySelector("head");
    document.getElementById("lnkFlavour")?.remove();

    if (flavour !== "default" && target) {
      const flavourURL = this.ResolveRelativeURL(`~/css/flavours/${flavour}.css`),
        existing = document.querySelector(`#lnkFlavour[href^='${flavourURL}']`);

      if (!existing) {
        document.getElementById("lnkFlavour")?.remove();

        target.appendChild(Object.assign(document.createElement("link"), {
          rel: "stylesheet",
          href: flavourURL,
          id: "lnkFlavour"
        }));
      } else {
        existing.href = flavourURL;
      }

      document.documentElement.dataset.flavour = flavour?.toLowerCase() ?? "";
    } else {
      document.documentElement.removeAttribute("data-flavour");
    }

    Auxilium.config.flavour = flavour?.toLowerCase() ?? "default";

    _setFlavourLocalization(flavour);
  }

  static PrintBlob(blob) {
    const iframe = document.createElementWithContents("iframe", null, {
      src: URL.createObjectURL(blob),
      className: "d-none",
      onload: () => iframe.contentWindow.print()
    });

    function cleanUp() {
      iframe.remove();
      window.removeEventListener("focus", cleanUp);
    }

    window.addEventListener("focus", cleanUp);

    document.body.appendChild(iframe);
  }

  static GetThemeColour = () => document.documentElement.dataset.colour?.toLowerCase() ?? "blue";

  static GetThemeColours = (includeCorporate) => ["blue", "red", "orange", "purple", "pink", "amber", "cyan", "green", "brown", "teal", "crimson", "olive", "gold", "mauve", "steel", "taupe"]
    .concat(includeCorporate ? ["chipside", "oppidatim"] : []);

  static GetFlavour() { return (localStorage.getItem("flavour") ?? "default").toLowerCase(); }

  static SetColourMode(theme, local) {
    theme = theme?.toLowerCase() ?? "";

    if (theme !== "system") {
      document.documentElement.dataset.theme = theme;

      if (!local) {
        localStorage.setItem("site-theme", theme);
      }

      const metaTarget = document.querySelector("meta[name='theme-color']");

      if (metaTarget) {
        metaTarget.setAttribute("content", theme === "dark" ? "#1D1E24" : "#FFFFFF");
      }
    } else {
      document.documentElement.removeAttribute("data-theme");

      if (!local) {
        localStorage.removeItem("site-theme");
      }
    }

    Auxilium.config.theme = theme;

    Razor.InvokeThemeChange();
  }

  static ToggleColourMode(local) {
    this.SetColourMode(document.documentElement.dataset.theme === "dark" ? "light" : "dark", local);
  }

  static TryParseJSON(value) {
    let obj = value;

    try {
      obj = JSON.parse(value);
    } catch {
      // *twiddle thumbs*
    }

    return obj;
  }

  static GetPaths = (url = "") => new URL(url || document.URL).pathname.toLowerCase().split("/").filter(x => x?.trim() !== "");

  static GetCookie = name => `; ${document.cookie}`.split(`; ${name}=`).pop().split(";").shift();

  static AddMonths(date, amount) {
    date.setDate(1);
    date.setMonth(date.getMonth() + amount);
    date.setDate(Math.min(date.getDate(), this.#GetDaysInMonth(date.getFullYear(), date.getMonth())));

    return date;
  }

  static AddYears(date, amount) {
    date.setFullYear(date.getFullYear() + amount);

    return date;
  }

  static #GetDaysInMonth = (year, month) => [31, (this.#IsLeapYear(year) ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
  static #IsLeapYear = year => (((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0));

  static #tryShowNotifications(title, options) {
    navigator.serviceWorker.ready.then(reg => {
      reg.getNotifications().then(notifications => {
        if (!notifications.find(x => x.tag === options.tag)) {
          reg.showNotification(title, options)
        }
      });
    });
  }

  static ShowNotification(title, options = {}) {
    options = {
      icon: "/favicon.ico",
      ...options
    };

    if (Notification.permission === "granted") {
      this.#tryShowNotifications(title, options);
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          this.#tryShowNotifications(title, options);
        }
      });
    }

  }

  static GetURLParameters = parameter => new URLSearchParams(location.search).getAll(parameter);
  static GetURLParameter = parameter => this.GetURLParameters(parameter).at(0);

  static SetURLParameters(parameters, ignoreIdentical) {
    const params = new URLSearchParams(location.search);

    if (parameters) {
      Object.keys(parameters).forEach(key => {
        let value = parameters[key];

        if (value?.trim?.() === "") {
          value = null;
        }

        if (value == null || (Array.isArray(value) && !value.length)) {
          params.delete(key);
        } else {
          const all = params.getAll(key).map(String);

          if (Array.isArray(value)) {
            params.delete(key);
            value.forEach(x => params.append(key, x));
          } else if (!all.includes(value.toString())) {
            params.set(key, value);
          }
        }
      });

      const newValue = params.toString(),
        newUrl = `${location.pathname}${newValue !== '' ? '?' + newValue : ''}`;

      if (newUrl === location.pathname + location.search && ignoreIdentical) { return false; }

      location.href = newUrl;

      return true;
    }

    return false;
  }

  static SetURLParameter = (name, value, ignoreIdentical) => this.SetURLParameters({ [name]: value }, ignoreIdentical);

  static ListenForShortcut(key, modifiers, callback, ignoreInputs) {
    if (typeof modifiers === "string") {
      modifiers = [modifiers];
    }

    if (typeof callback !== "function") { return; }

    window.addEventListener("keydown", event => {
      if (ignoreInputs && (event.target ?? event.relatedTarget)?.closest("input, textarea")) { return; }

      if (event.key.toUpperCase() === key.toUpperCase() && !document.querySelector(".modal.show")) {
        if (modifiers.length) {
          const modifierChecker = {
            CTRL: event.ctrlKey,
            ALT: event.altKey,
            SHIFT: event.shiftKey,
            META: event.metaKey
          }

          if (modifierChecker && !modifiers.find(x => !modifierChecker[x])) {
            const keys = Object.keys(modifierChecker);

            if (keys && !keys.filter(x => !modifiers.includes(x)).find(x => !!modifierChecker[x])) {
              event.preventDefault();
              callback(event);
            }
          }
        } else {
          event.preventDefault();
          callback(event);
        }
      }
    });
  }
}
